import classnames from 'classnames';
import React, { FunctionComponent, useEffect, useState } from 'react';
import useRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PrimaryToolTip } from '../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { useFreeTrialUpgradeModal } from '../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import {
  isBilling2023Subscription,
  SubscriptionModel,
} from '@mm/state/model/SubscriptionModel';
import './OveragesManagement.pcss';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { landmineNames } from '@mm/constants/landmine-names';
import { Tooltip } from '@mm/components';

type Props = {
  facebookPage?: EnhancedBillingPage;
  subscription?: SubscriptionModel;
  textOverride?: string;
  hideQuestionIcon?: boolean;
  className?: string;
  showLink?: boolean;
  navigationPath?: string;
  buttonColor?: ButtonColor;
  disableOverages?: boolean;
};

const OveragesManagement: FunctionComponent<Props> = props => {
  const {
    facebookPage,
    subscription,
    hideQuestionIcon,
    className,
    textOverride,
    showLink,
    navigationPath,
    children,
    buttonColor,
    disableOverages,
  } = props;

  const router = useRouter();

  const {
    isFreeTrialPromotionWithoutSubscription,
    isFreeTrialPromotionWithFutureSubscription,
    openFreeTrialUpgradeModal,
  } = useFreeTrialUpgradeModal(facebookPage);
  const { sendOveragesEnabled } = useSubscriptionCRUDActions();
  const isBilling2023Plan =
    subscription && isBilling2023Subscription(subscription);

  const defaultEnabled = facebookPage
    ? facebookPage.overagesEnabled
    : (subscription as SubscriptionModel).overagesEnabled;
  const [overagesEnabled, setOveragesEnabled] = useState(defaultEnabled);
  const [makingRequest, setMakingRequest] = useState(false);
  const text =
    textOverride ||
    `Automatically increase send credit limit when current allotment is exceeded?
    You will receive an email and your credit card will be charged for each new credit increase.`;
  const classList = classnames('overages-enabled__wrapper', className);

  const changeOveragesEnabled = async () => {
    const value = !overagesEnabled;
    const subscriptionId = subscription
      ? subscription.id
      : (
          (facebookPage as EnhancedBillingPage)
            .subscription as SubscriptionModel
        )?.id;
    setOveragesEnabled(value);
    setMakingRequest(true);
    let response;
    if (subscriptionId && facebookPage?.id) {
      response = await sendOveragesEnabled(
        subscriptionId,
        value,
        facebookPage.id,
        undefined,
        undefined,
        undefined,
        !!isBilling2023Plan,
      );
    }

    if (response && response.success) {
      setMakingRequest(false);
    } else {
      setMakingRequest(false);
      setOveragesEnabled(
        response && response.overagesEnabled
          ? response.overagesEnabled
          : !value,
      );
    }
  };

  const navigateTo = () => {
    if (facebookPage && facebookPage.hardLimitReached) {
      router.history.push('/settings/billing');
      return;
    }
    if (navigationPath) {
      router.history.push(navigationPath);
    }
  };

  const getText = () => {
    if (facebookPage?.redemption?.freeTrial && !facebookPage?.subscription) {
      return `Upgrade now`;
    }

    return facebookPage?.hardLimitReached ? `Purchase Credits` : `Upgrade now`;
  };

  const onClick = () => {
    if (isFreeTrialPromotionWithoutSubscription) {
      openFreeTrialUpgradeModal({
        landmineName: landmineNames.OVERAGES_MANAGMENT,
      });
    } else {
      navigateTo();
    }
  };

  useEffect(() => {
    setOveragesEnabled(facebookPage?.overagesEnabled);
  }, [facebookPage?.overagesEnabled]);

  if (
    isFreeTrialPromotionWithFutureSubscription ||
    (subscription && subscription.expiresAt)
  ) {
    return null;
  }

  return (
    <div className={classList}>
      {!showLink && (
        <input
          type="checkbox"
          disabled={makingRequest || disableOverages}
          className="overages-enabled__checkbox"
          checked={overagesEnabled}
          onChange={changeOveragesEnabled}
        />
      )}

      {!showLink && (
        <div
          className={classnames('overages-enabled__text', {
            'overages-enabled__text-disabled': disableOverages,
          })}
        >
          {text}
          {!hideQuestionIcon && !isFreeTrialPromotionWithoutSubscription && (
            <Tooltip
              title={
                <div>
                  Overages Enabled
                  <span>
                    You will be automatically upgraded to a new level when the
                    limit is reached.
                  </span>
                </div>
              }
              placement="bottom"
            >
              Don't detect this keyword in the middle of an active conversation
              {children}
            </Tooltip>
          )}
        </div>
      )}

      {showLink && (
        <PrimaryButton
          className="overages-enabled__button"
          color={buttonColor || ButtonColor.transparent}
          onClick={onClick}
        >
          {getText()}
        </PrimaryButton>
      )}
    </div>
  );
};

export default OveragesManagement;
